import { finalSaleReturnsCoverageCalculator } from "@redotech/redo-model/coverage-calculator/final-sale-returns";
import {
  CustomerInfo,
  ItemInfo,
} from "@redotech/redo-model/coverage-calculator/index";
import { packageProtectionCoverageCalculator } from "@redotech/redo-model/coverage-calculator/package-protection";
import { returnCoverageCalculator } from "@redotech/redo-model/coverage-calculator/returns";
import { CoverageInfo, WidgetAutocheck } from "@redotech/redo-model/widget";
import {
  ExtensionShopifyClient,
  ShopifyCart,
  ShopifyCartItem,
} from "@redotech/shopify-client/ajax";
import { isDef } from "@redotech/util/checks";
import { ShopifyExtensionClient } from "../client/shopify-extension-client";
import { autocheckToCoverageSettings } from "../eligibility/autocheck-conversion";
import { getProductInfoFromSource } from "../eligibility/returns-coverage-eligibility";
import { log } from "../monitoring/log";
import { coveragePriceCache } from "./checkout-buttons/coverage-price-cache";
import { loadCheckoutProbabilityFromSessionStorage } from "./checkout-probability";
import { selectBetaDraw } from "./select-beta-draw";

async function cartItemToItemInfo({
  cartItem,
  autocheck,
  shopifyClient,
  redoClient,
  onTapcart,
}: {
  cartItem: ShopifyCartItem;
  autocheck: WidgetAutocheck;
  shopifyClient: ExtensionShopifyClient;
  redoClient: ShopifyExtensionClient;
  onTapcart: boolean;
}): Promise<ItemInfo> {
  const { tags, collections } = await getProductInfoFromSource({
    cartItem,
    autocheck,
    shopifyClient,
    redoClient,
  });

  const properties: string[] = cartItem.properties
    ? Object.keys(cartItem.properties).reduce((acc, key) => {
        const value = cartItem.properties?.[key];
        acc.push(key);
        if (value !== null && value !== "") {
          acc.push(`${key}: ${value}`);
        }
        return acc;
      }, [] as string[])
    : [];

  let price =
    cartItem.discounted_price ||
    (cartItem.line_price ? cartItem.line_price / cartItem.quantity : 0);
  price = onTapcart ? price : price / 100;

  let preDiscountPrice =
    cartItem.original_price ||
    (cartItem.original_line_price
      ? cartItem.original_line_price / cartItem.quantity
      : 0);
  preDiscountPrice = onTapcart ? preDiscountPrice : preDiscountPrice / 100;

  return {
    lineItemId: cartItem.id?.toString() ?? "",
    price,
    preDiscountPrice,
    quantity: cartItem.quantity,
    collections: collections.map((c) => c.title),
    productTags: tags,
    properties,
    vendor: cartItem.vendor || "",
    discounts: cartItem.discounts?.map((d) => ({ name: d.title })) || [],
    isFinalSale: false,
    hasSubscription: !!cartItem.selling_plan_allocation?.selling_plan?.id,
    deliveryProfileId: null,
    requiresShipping: cartItem.requires_shipping ?? true,
  };
}

function convertToCents({
  dollars,
  onTapcart,
}: {
  dollars: number | undefined;
  onTapcart: boolean;
}) {
  if (!dollars) return 0;

  // tapcart money stays in dollars.
  if (onTapcart) return dollars;

  // round to nearest cent
  return Math.round(dollars * 100);
}

export async function getCoverageInfoFromCoverageCalculators({
  autocheck,
  customerInfo,
  cart,
  shopifyClient,
  redoClient,
  onTapcart,
}: {
  autocheck: WidgetAutocheck;
  customerInfo: CustomerInfo;
  cart: ShopifyCart;
  shopifyClient: ExtensionShopifyClient;
  redoClient: ShopifyExtensionClient;
  onTapcart: boolean;
}) {
  // Select a per-shopper beta draw from the posterior subsample (if stagecoach is active)
  const mlModel = autocheck?.packageProtection?.coverageMlModel;
  if (mlModel?.id === "stagecoach") {
    const stagecoachConfig = mlModel.config?.stagecoachInferenceConfig;
    if (stagecoachConfig?.posteriorSubsample) {
      const draw = selectBetaDraw({
        posteriorSubsample: stagecoachConfig.posteriorSubsample,
        modelVersionId: stagecoachConfig.modelVersionId,
      });
      // Mutates stagecoachConfig in-place on the cached autocheck object.
      // This is safe because selectBetaDraw is idempotent (persisted in localStorage),
      // and no other code reads betaVector/betaSampleIndex from the autocheck cache.
      if (draw) {
        stagecoachConfig.betaSampleIndex = draw.betaSampleIndex;
        stagecoachConfig.betaVector = draw.betaVector;
      }
    }
  }

  const teamSettings = autocheckToCoverageSettings(autocheck);

  const nonRedoItems = cart.items.filter((item) => item.vendor !== "re:do");
  const items = await Promise.all(
    nonRedoItems.map((item) =>
      cartItemToItemInfo({
        cartItem: item,
        autocheck,
        shopifyClient,
        redoClient,
        onTapcart,
      }),
    ),
  );

  const cachedCoveragePrices = coveragePriceCache.getCachedCoveragePrices({
    cartItems: cart.items,
  });

  const checkoutProbability = (() => {
    const stored = loadCheckoutProbabilityFromSessionStorage();
    if (isDef(stored?.cumulativeConversionProbability)) {
      // Convert from 0-1 scale to 0-100 scale
      return stored.cumulativeConversionProbability * 100;
    }
    return undefined;
  })();

  const returnCoverageInfo = returnCoverageCalculator.getCoverage({
    team: teamSettings,
    customerInfo,
    items,
    commentsoldIntegration: null,
    ignoreMerchantPaidForEnablementCheck: false,
  });
  const packageProtectionCoverageInfo =
    packageProtectionCoverageCalculator.getCoverage({
      team: teamSettings,
      customerInfo,
      items,
      commentsoldIntegration: null,
      ignoreMerchantPaidForEnablementCheck: false,
      priceOverrideForCoverageMlModelCacheOnly:
        cachedCoveragePrices?.packageProtection,
      priceCalculationContext: { checkoutProbability },
    });
  const finalSaleReturnsCoverageInfo =
    finalSaleReturnsCoverageCalculator.getCoverage({
      team: teamSettings,
      customerInfo,
      items,
      commentsoldIntegration: null,
      ignoreMerchantPaidForEnablementCheck: false,
    });

  coveragePriceCache.maybeCacheCoveragePrices({
    cartItems: cart.items,
    coveragePrices: {
      returns: returnCoverageInfo.priceResult,
      packageProtection: packageProtectionCoverageInfo.priceResult,
      finalSaleReturns: finalSaleReturnsCoverageInfo.priceResult,
    },
  });

  const returnPrice = convertToCents({
    dollars: returnCoverageInfo.priceResult?.price,
    onTapcart,
  });
  const packageProtectionPrice = convertToCents({
    dollars: packageProtectionCoverageInfo.priceResult?.price,
    onTapcart,
  });
  const finalSaleReturnsPrice = convertToCents({
    dollars: finalSaleReturnsCoverageInfo.priceResult?.price,
    onTapcart,
  });

  // return coverage is included in final sale coverage. So we need to take the max of the two.
  const returnCoveragePrice = Math.max(returnPrice, finalSaleReturnsPrice);

  const bothEligible =
    (returnCoverageInfo.eligible || finalSaleReturnsCoverageInfo.eligible) &&
    packageProtectionCoverageInfo.eligible;
  const bothPrice = packageProtectionPrice + returnCoveragePrice;
  const coverageInfo: CoverageInfo = {
    returns: {
      cartEligible:
        returnCoverageInfo.eligible || finalSaleReturnsCoverageInfo.eligible,
      price: returnCoveragePrice,
      isFinalSale: finalSaleReturnsCoverageInfo.eligible,
      reasonIneligible: returnCoverageInfo.reasonIneligible,
    },
    packageProtection: {
      cartEligible: packageProtectionCoverageInfo.eligible,
      price: packageProtectionPrice,
      reasonIneligible: packageProtectionCoverageInfo.reasonIneligible,
    },
    both: {
      cartEligible: bothEligible,
      price: bothEligible ? bothPrice : 0,
      reasonIneligible: bothEligible
        ? undefined
        : returnCoverageInfo.reasonIneligible ||
          packageProtectionCoverageInfo.reasonIneligible,
    },
  };

  return coverageInfo;
}

export function compareCoverageInfos({
  oldCoverageInfo,
  newCoverageInfo,
  autocheck,
  cart,
}: {
  oldCoverageInfo: CoverageInfo;
  newCoverageInfo: CoverageInfo;
  autocheck: WidgetAutocheck;
  cart: ShopifyCart;
}) {
  // remove reasonIneligible from newCoverageInfo to avoid comparing the reasonIneligible strings
  newCoverageInfo.returns.reasonIneligible = undefined;
  newCoverageInfo.packageProtection.reasonIneligible = undefined;
  newCoverageInfo.both.reasonIneligible = undefined;

  if (JSON.stringify(oldCoverageInfo) === JSON.stringify(newCoverageInfo)) {
    log("Coverage infos are the same");
    return true;
  }

  log("Coverage infos are different", { oldCoverageInfo, newCoverageInfo });
  return false;
}
